import type {
  ShadowStructure,
  TimelineAxisEntry,
  TimelineHighlight,
  TimelineSnapshot,
} from '@lib/api-types/views';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRepositories } from '../../api.ts';
import { ControlPanel } from '../components/ControlPanel.tsx';
import { ConnectReposEmpty, EmptyState } from '../components/EmptyState.tsx';
import {
  FloatingToolbar,
  GroupSkillsIcon,
  ShowLibrariesIcon,
  UltraCompactIcon,
  ToolbarMultiSelect,
  ToolbarSelect,
  ToolbarSeparator,
  ToolbarToggle,
} from '../components/FloatingToolbar.tsx';
import { MatrixCell } from '../components/MatrixCell.tsx';
import { ErrorView, Skeleton } from '../components/Skeleton.tsx';
import { TimelineScrubber } from '../components/TimelineScrubber.tsx';
import {
  ColumnHeader,
  ConnectorCell,
  GroupedCompLabel,
  PropIndicator,
  TruncatedLabel,
  nestVariant,
} from '../components/TruncatedLabel.tsx';
import {
  RATING_LABELS,
  TALENT_LEVELS,
  buildDerivedContextFunctionGate,
  buildRatingIndex,
  buildSkillFunctions,
  canUltraCompact,
  compRowHeight,
  coverageForCell,
  diffSnapshots,
  indexSkills,
  isCompactCatalog,
  isInstitutional,
  levelClass,
  mergeSnapshotsForDiff,
  NO_EXPANDED,
  remapSkillFunctionsToParents,
  rollupColumns,
  ultraCompactRowHeight,
  type BusinessFunction,
  type DerivedContext,
  type DerivedContextFunctionGate,
  type Engineer,
  type RatingValue,
  type RollupColumn,
  type SnapshotDiff,
} from '../data-views.ts';
import { sortByClientDistance } from '../derived-context-order.ts';
import { useFillViewportHeight } from '../hooks/useFillViewportHeight.ts';
import {
  useActivityView,
  useHistoryStatus,
  useTimelineAxis,
  useTimelineSnapshot,
  useViewQueryState,
} from '../hooks/useViews.ts';
import { makeMergedCoverageCell } from '../matrix-cells-views.ts';
import { usePersonFilter } from '../person-filter.ts';
import { useTeamFilter } from '../team-filter.ts';
import { useTechFilter } from '../tech-filter.ts';

import { ActivityList } from './ActivityView.tsx';
import { ProfileMatrixGrid } from './TalentProfileMap.tsx';

const SUBTITLE =
  'Point-in-time snapshots of the platform map. Drag the slider to scrub through history, or press play to tour the highlights.';

// Activity feed (the right-hand control panel on the Timeline / history
// page) is hidden for now. Kept wired up — flip this to `true` to bring it
// back. The activity data is still fetched so restoring is a one-line change.
const SHOW_ACTIVITY_FEED = false;

const SNAPSHOT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Format a snapshot date as "Feb-02-2026" (Mon-DD-YYYY). The value
// arrives as a YYYY-MM-DD prefix (possibly a full ISO string); parse the
// date parts directly rather than via `new Date()` so a UTC-midnight
// timestamp doesn't shift a day in the local timezone.
function formatSnapshotDate(date: string | null | undefined): string {
  if (!date) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  return `${SNAPSHOT_MONTHS[Number(m[2]) - 1]}-${m[3]}-${m[1]}`;
}

// Normalise a snapshot date to its YYYY-MM-DD prefix for equality checks —
// axis ticks and per-snapshot payloads may carry a full ISO timestamp.
function dateKey(date: string | null | undefined): string | null {
  return date ? date.slice(0, 10) : null;
}

// Long form ("Feb 24, 2026") for the snapshot-notes panel heading.
function formatLongSnapshotDate(date: string | null | undefined): string {
  if (!date) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  return `${SNAPSHOT_MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

type NoteTone = 'comp' | 'skill' | 'team' | 'depr';
interface SnapshotNote {
  tone: NoteTone;
  text: string;
}

// Join a short list as "A", "A and B", or "A, B and C".
function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function capitalize(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

// A roster delta reads as a name list up to 5 people; beyond that a full list
// is noise, so collapse to a bare count ("7 contributors").
const CONTRIBUTOR_LIST_MAX = 5;
function contributorList(names: string[]): string {
  return names.length > CONTRIBUTOR_LIST_MAX
    ? `${names.length} contributors`
    : joinList(names);
}

/**
 * Derive a "what changed this snapshot" readout from the data the timeline
 * view actually carries: the current↔prior structural diff (derivedContexts /
 * skills) plus the per-snapshot engineer roster.
 *
 * Deliberately scoped to what reaches the frontend today. Commit-volume /
 * churn-burst figures (e.g. "April 2024 burst, 602 commits") and file-level
 * summaries live in backend tables (commit_summaries, churn) that
 * /views/timeline doesn't expose yet — surfacing those would need new
 * per-snapshot fields on the timeline payload.
 */
function deriveSnapshotNotes(
  current: TimelineSnapshot | undefined,
  prior: TimelineSnapshot | null | undefined,
  diff: SnapshotDiff,
  highlight: TimelineHighlight | null,
): { topline: string; notes: SnapshotNote[] } {
  // A highlight (the pop-scientist narrative) always wins the topline, and it
  // rides the axis — so it's known from first paint, before the per-snapshot
  // payload or `prior` finish loading. Preferring it in the early-return paths
  // below is what stops the "regular view flashes, then the highlight
  // supersedes it" jump when scrubbing onto a highlighted date.
  const highlightTopline = highlight?.summary || highlight?.title || null;
  if (!current) return { topline: highlightTopline ?? '—', notes: [] };
  if (!prior) {
    return {
      topline: highlightTopline ?? 'First recorded snapshot of the platform map.',
      notes: [],
    };
  }

  const newComps = current.derivedContexts
    .filter((c) => diff.newCompIds.has(c.id))
    .map((c) => c.display);
  const removedComps = prior.derivedContexts
    .filter((c) => diff.removedCompIds.has(c.id))
    .map((c) => c.display);
  const newSkills = diff.newSkillIds.size;
  const removedSkills = diff.removedSkillIds.size;

  // The engineer roster is per-snapshot (humans only — agents aren't
  // "contributors"). A NEW contributor is one whose first commit (`started_at`)
  // falls after the prior snapshot — the honest signal, and robust to roster
  // churn: an engineer can drop out of a snapshot for a non-`started_at` reason
  // (status → 'review', or losing observation coverage in an era) and re-enter,
  // which a bare id-diff miscounts as "new" (this is what inflated org-58's "83
  // new"). Fall back to the id-diff only when `started_at` is unknown.
  const priorById = new Map(prior.engineers.map((e) => [e.id, e]));
  const currentIds = new Set(current.engineers.map((e) => e.id));
  const priorDateSec = prior.date ? Math.floor(Date.parse(prior.date) / 1000) : 0;
  const joined = current.engineers.filter((e) => {
    if (e.kind !== 'human') return false;
    if (e.started_at != null && Number.isFinite(priorDateSec)) {
      return e.started_at > priorDateSec;
    }
    return !priorById.has(e.id);
  });
  const departed = [
    ...prior.engineers.filter(
      (e) =>
        e.kind === 'human' && e.status === 'active' && !currentIds.has(e.id),
    ),
    ...current.engineers.filter(
      (e) =>
        e.kind === 'human' &&
        e.status === 'departed' &&
        priorById.get(e.id)?.status === 'active',
    ),
  ];

  const notes: SnapshotNote[] = [];
  if (newComps.length) {
    notes.push({ tone: 'comp', text: `${joinList(newComps)} came online` });
  }
  if (newSkills) {
    notes.push({
      tone: 'skill',
      text: `${newSkills} new skill${newSkills === 1 ? '' : 's'}`,
    });
  }
  if (joined.length) {
    notes.push({
      tone: 'team',
      text: `${contributorList(joined.map((e) => e.display_name))} joined`,
    });
  }
  if (removedComps.length) {
    notes.push({
      tone: 'depr',
      text: `${removedComps.length} context${
        removedComps.length === 1 ? '' : 's'
      } deprecated`,
    });
  }
  if (removedSkills) {
    notes.push({
      tone: 'depr',
      text: `${removedSkills} skill${removedSkills === 1 ? '' : 's'} deprecated`,
    });
  }
  if (departed.length) {
    notes.push({
      tone: 'team',
      text: `${contributorList(
        departed.map((e) => e.display_name),
      )} stopped actively contributing`,
    });
  }

  // Prefer a server-computed highlight summary when one exists; else
  // assemble a generic topline from the structural counts.
  const summaryParts: string[] = [];
  if (newComps.length)
    summaryParts.push(
      `${newComps.length} new context${newComps.length === 1 ? '' : 's'}`,
    );
  if (newSkills)
    summaryParts.push(`${newSkills} new skill${newSkills === 1 ? '' : 's'}`);
  if (joined.length)
    summaryParts.push(
      `${joined.length} new contributor${joined.length === 1 ? '' : 's'}`,
    );
  const priorDate = formatLongSnapshotDate(prior.date);
  const generated = summaryParts.length
    ? `${capitalize(joinList(summaryParts))} since ${priorDate}.`
    : `No structural changes to the platform map since ${priorDate}.`;
  const topline = highlightTopline || generated;

  return { topline, notes };
}

/**
 * Right-hand panel summarising what changed in the selected snapshot:
 * a topline sentence + a few areas of analysis derived from real data.
 */
function SnapshotNotes({
  date,
  topline,
  notes,
  structure,
}: {
  date: string;
  topline: string;
  notes: SnapshotNote[];
  structure?: ShadowStructure;
}) {
  return (
    <aside className='snap-snapshot-notes'>
      <div className='snap-snapshot-notes__date'>{date}</div>
      <p className='snap-snapshot-notes__desc'>{topline}</p>
      {notes.length > 0 ? (
        <ul className='snap-snapshot-notes__list'>
          {notes.map((n, i) => (
            <li
              key={i}
              className={`snap-snapshot-notes__item snap-snapshot-notes__item--${n.tone}`}
            >
              {n.text}
            </li>
          ))}
        </ul>
      ) : null}
      {structure && structure.derivedContexts.length > 0 ? (
        <div className='snap-snapshot-notes__structure'>
          <div className='snap-snapshot-notes__structure-head'>Co-change structure (evidence)</div>
          <ul className='snap-snapshot-notes__list'>
            {structure.derivedContexts.slice(0, 12).map((c) => (
              <li key={c.id} className='snap-snapshot-notes__item'>
                {c.display}
                {c.coupled_to ? (
                  <span className='snap-snapshot-notes__coupling'>
                    {` — ${c.coupling}% coupled to ${c.coupled_to}`}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </aside>
  );
}

// Stable empty-array refs for the no-current-snapshot fallback (avoids
// busting useMemo deps in TimelineBody when the slider hasn't moved
// but the snapshots list changes shape).
const EMPTY_ENGINEERS: Engineer[] = [];
const EMPTY_RATINGS: import('../data-views.ts').Rating[] = [];

export function TimelineView() {
  // The lightweight axis (dates + highlights + newest snapshot embedded); the
  // rest of the snapshots load lazily in TimelineBody. Memory-safe for any org.
  // The server picks the adaptive, history-length-based tick cadence — there is
  // no user-facing granularity / date-range control.
  const { data: axisData, isLoading, error } = useTimelineAxis();
  const {
    data: activity,
    fetchNextPage: fetchNextActivity,
    hasNextPage: hasMoreActivity,
    isFetchingNextPage: isFetchingMoreActivity,
    // Gated on the feature flag: the activity route is uncached and runs a
    // full compose per page — don't fetch it for a component that doesn't
    // render (perf deep-dive §4.3). Falls back to axisData below either way.
  } = useActivityView({ enabled: SHOW_ACTIVITY_FEED });

  if (error) {
    return <ErrorView title='Timeline' error={error} />;
  }
  if (isLoading || !axisData) {
    return <Skeleton title='Timeline' subtitle={SUBTITLE} rows={8} />;
  }

  // Flatten the infinite-query pages into a single ordered list so
  // ActivityList (used both here and in /activity) gets the same shape
  // as before. Pagination state forwards through to its See-more
  // footer.
  const activityFirstPage = activity?.pages[0];
  const activityItems = activity?.pages.flatMap((p) => p.items) ?? [];

  return (
    <TimelineBody
      axis={axisData.axis}
      latest={axisData.latest}
      activityItems={activityItems}
      activityDerivedContexts={activityFirstPage?.derivedContexts ?? axisData.latest?.derivedContexts ?? []}
      activityFunctions={activityFirstPage?.business_functions ?? axisData.latest?.business_functions ?? []}
      hasMoreActivity={Boolean(hasMoreActivity)}
      isLoadingMoreActivity={isFetchingMoreActivity}
      onLoadMoreActivity={() => void fetchNextActivity()}
    />
  );
}

function TimelineBody({
  axis,
  latest,
  activityItems,
  activityDerivedContexts,
  activityFunctions,
  hasMoreActivity,
  isLoadingMoreActivity,
  onLoadMoreActivity,
}: {
  axis: TimelineAxisEntry[];
  latest: TimelineSnapshot | null;
  activityItems: import('@lib/api-types/views').ActivityItem[];
  activityDerivedContexts: DerivedContext[];
  activityFunctions: BusinessFunction[];
  hasMoreActivity: boolean;
  isLoadingMoreActivity: boolean;
  onLoadMoreActivity: () => void;
}) {
  // Slider lands on the newest entry; `safeIndex` defends against the axis
  // shrinking after mount (shouldn't normally happen).
  const [index, setIndex] = useState(() => Math.max(0, axis.length - 1));
  // Whether the cursor is still "following the present" — true until the user
  // scrubs off the newest tick. Gates the re-pin-on-axis-change below so a
  // background axis reshape can't yank a user who has scrubbed into history.
  const followNewestRef = useRef(true);

  const axisRef = useRef(axis);
  axisRef.current = axis;

  // Re-pin to the newest tick when a new axis lands, but *only while following
  // the present*: `index` is a raw array position, so an axis that reshapes
  // after mount — a completing backfill can prepend older dates — would
  // otherwise strand the cursor on a now-older date and diff its freshly-loaded
  // snapshot against a stale newest `prior`, flashing "everything deprecated".
  // Once the user scrubs, `followNewestRef` is false and we leave their
  // position alone. MAX_SAFE_INTEGER clamps to the last entry via `safeIndex`.
  useEffect(() => {
    if (followNewestRef.current) {
      setIndex(Number.MAX_SAFE_INTEGER);
    }
  }, [axis]);

  // Scrubbing (drag / click / keyboard / autoplay) records whether we're still
  // on the newest tick, so a later axis reshape respects a user who has moved
  // into history. Stable identity (reads the axis via ref) keeps the scrubber's
  // autoplay effect from re-arming.
  const handleIndexChange = useCallback((next: number) => {
    followNewestRef.current = next >= axisRef.current.length - 1;
    setIndex(next);
  }, []);
  const [talentLevel, setTalentLevel] = useState<RatingValue>(2);
  const [grouped, setGrouped] = useState(true);
  // Per-derivedContext skill expand/collapse — click a derivedContext's connector line
  // to list its skills (or fold them back), the same affordance the /setup
  // editor uses. `grouped` is the baseline for every derivedContext; `expandOverrides`
  // holds the ids that differ from it, so "Group Skills" reads as collapse/
  // expand-all while individual connectors fine-tune single derivedContexts.
  const [expandOverrides, setExpandOverrides] = useState<Set<string>>(new Set());
  const isCompExpanded = useCallback(
    (id: string) => (expandOverrides.has(id) ? grouped : !grouped),
    [expandOverrides, grouped],
  );
  const toggleCompExpanded = useCallback((id: string) => {
    setExpandOverrides((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  // Flipping the master toggle re-baselines every derivedContext (clears overrides).
  const setAllGrouped = useCallback((g: boolean) => {
    setGrouped(g);
    setExpandOverrides(new Set());
  }, []);
  const techFilter = useTechFilter();
  const teamFilter = useTeamFilter();
  // Ultra-compact rows — off by default, offered only on a catalog big enough
  // to need it (see `canUltraCompact`).
  const [ultra, setUltra] = useState(false);

  // Keep polling /history/status so freshly-backfilled highlights land on
  // the scrubber as soon as they're ready — the hook invalidates the
  // timeline query on the in-flight → done transition (see useHistoryStatus).
  useHistoryStatus();

  // "No repos linked yet" is a distinct empty state from "this date has
  // no snapshot": the former invites the user to connect a repo, the
  // latter just nudges the slider. Drive it off the repositories list so
  // we don't mistake a mid-backfill gap (repos exist, snapshots pending)
  // for a fresh org. Skipped in mock/demo mode, which always has data.
  const { useMock, slug } = useViewQueryState();
  const { data: repositories } = useRepositories(useMock ? null : slug);
  const noRepos = !useMock && !!slug && repositories?.length === 0;

  const safeIndex = Math.min(index, Math.max(0, axis.length - 1));
  const currentDate = axis[safeIndex]?.date ?? null;
  const priorDate = safeIndex > 0 ? (axis[safeIndex - 1]?.date ?? null) : null;
  const nextDate = safeIndex < axis.length - 1 ? (axis[safeIndex + 1]?.date ?? null) : null;
  const isNewest = axis.length > 0 && safeIndex === axis.length - 1;

  // Lazy per-snapshot loads. The newest date is embedded in the axis (`latest`),
  // so we skip its fetch; `prior` feeds the current↔prior diff; `next` is
  // prefetched so forward scrubbing + autoplay are instant. All react-query-cached.
  const currentQuery = useTimelineSnapshot(isNewest ? null : currentDate);
  const priorQuery = useTimelineSnapshot(priorDate);
  useTimelineSnapshot(nextDate); // prefetch only

  const currentBase = isNewest ? latest : (currentQuery.data ?? null);
  const currentLoading = !isNewest && currentDate !== null && currentQuery.isLoading;
  const currentHighlight = axis[safeIndex]?.highlight ?? null;
  // Re-attach the highlight from the axis (the per-snapshot payload omits it).
  const current = useMemo(
    () => (currentBase ? { ...currentBase, highlight: currentHighlight } : undefined),
    [currentBase, currentHighlight],
  );
  // Date-guard the current↔prior pairing. `useTimelineSnapshot` keeps the
  // previous date's snapshot on screen via react-query `placeholderData` while
  // the next one loads — and still returns it on the oldest tick, where
  // `priorDate` is null. Diffing a freshly-loaded `current` against a stale
  // `prior` from a *different* date marks every current derivedContext and
  // contributor as removed/departed (the confusing first-load flash). Only
  // treat `prior` as real when both snapshots carry the dates we asked for.
  const prior = useMemo(() => {
    const raw = priorQuery.data ?? null;
    if (!raw || priorDate == null || !current) return null;
    if (dateKey(raw.date) !== dateKey(priorDate)) return null;
    if (dateKey(current.date) !== dateKey(currentDate)) return null;
    return raw;
  }, [priorQuery.data, priorDate, current, currentDate]);

  // Merge current + prior so deprecated items stay visible (rendered
  // with strikethrough + diagonal-stripe cells). The diff drives the
  // new / deprecated visual indicators.
  const merged = useMemo(() => {
    if (!current) return { derivedContexts: [], business_functions: [] };
    return mergeSnapshotsForDiff(
      { derivedContexts: current.derivedContexts, business_functions: current.business_functions },
      prior
        ? { derivedContexts: prior.derivedContexts, business_functions: prior.business_functions }
        : null,
    );
  }, [current, prior]);

  const diff = useMemo(() => {
    if (!current) return diffSnapshots({ derivedContexts: [], business_functions: [] }, null);
    return diffSnapshots(
      { derivedContexts: current.derivedContexts, business_functions: current.business_functions },
      prior
        ? { derivedContexts: prior.derivedContexts, business_functions: prior.business_functions }
        : null,
    );
  }, [current, prior]);

  const snapshotNotes = useMemo(
    () => deriveSnapshotNotes(current, prior, diff, currentHighlight),
    [current, prior, diff, currentHighlight],
  );

  const comps = merged.derivedContexts;
  const fns = merged.business_functions;

  // Scrubbing changes the context count, so ultra-compact's eligibility moves
  // with the date. `ultra` is the user's latched preference; `ultraOn` is
  // whether it actually applies here — without the second one, scrubbing back
  // to an early era (3 contexts) would keep rendering 12px bands with no
  // toolbar control left to turn them off.
  const ultraAvailable = canUltraCompact(comps);
  const ultraOn = ultra && ultraAvailable;

  // Stable empty-array refs so `useMemo` deps below don't bust on
  // every render when there's no current snapshot.
  const personFilter = usePersonFilter(current?.engineers ?? EMPTY_ENGINEERS);
  const team: Engineer[] = useMemo(
    () => personFilter.filter(teamFilter.filter(current?.engineers ?? EMPTY_ENGINEERS)),
    [personFilter, teamFilter, current],
  );
  const ratings = useMemo(
    () => current?.ratings ?? EMPTY_RATINGS,
    [current],
  );

  // Single-person mode: when exactly one person is picked, the Timeline turns
  // into that IC's green proficiency map, time-scrubbed by the slider. Resolve
  // their identity from the newest snapshot (embedded in the axis), falling
  // back to the current one — so scrubbing to a date before they joined keeps
  // the single-person layout (the map just goes empty) rather than snapping
  // back to the team view.
  const selectedPersonId =
    personFilter.selected.length === 1 ? personFilter.selected[0] : null;
  const profileEngineer = useMemo(() => {
    if (!selectedPersonId) return null;
    return (
      latest?.engineers.find((e) => e.id === selectedPersonId) ??
      current?.engineers.find((e) => e.id === selectedPersonId) ??
      null
    );
  }, [selectedPersonId, latest, current]);

  const servedComps = useMemo(
    () =>
      // Client-distance order (client-facing first) is the default row order.
      sortByClientDistance(comps)
        .map((comp) => ({
          ...comp,
          skills: comp.skills.filter(techFilter.visible),
          // Pre-filter length, kept for ultra-compact sizing — what makes the
          // ultra layout invariant under "Show Libraries". See
          // `ultraCompactRowHeight`.
          allSkillCount: comp.skills.length,
        }))
        .filter((comp) => comp.skills.length > 0),
    [comps, techFilter],
  );

  const skillsById = useMemo(() => indexSkills(servedComps), [servedComps]);
  const skillFunctions = useMemo(() => buildSkillFunctions(servedComps), [servedComps]);
  const ratingIndex = useMemo(
    () => buildRatingIndex(skillsById, team, ratings),
    [skillsById, team, ratings],
  );

  // The column axis: the promoted, top-level functions, each parent collapsed
  // over its children, plus the child→parent adjacency remap so a group column
  // aggregates coverage across the whole group.
  const columns = useMemo<RollupColumn[]>(
    () => rollupColumns(fns, NO_EXPANDED),
    [fns],
  );
  const skillFunctionsGrouped = useMemo(
    () => remapSkillFunctionsToParents(skillFunctions, fns),
    [skillFunctions, fns],
  );
  // Shared platform-map cell gate — a (comp × fn) cell exists only where the
  // derivedContext itself serves the function, never via the per-skill union.
  const cfGate = useMemo(
    () => buildDerivedContextFunctionGate(servedComps, fns),
    [servedComps, fns],
  );

  // HIDE ROWS WITH NO CELLS AT ALL — always, with no toolbar control. Scrubbing to
  // an early date leaves most rows structurally empty (the skill exists in today's
  // catalog but served nothing back then), and a grid that is mostly blank reads as
  // noise rather than as the codebase growing into its surfaces. This used to be an
  // opt-in toggle defaulting off; nobody wants the blank rows, so it is the only
  // behaviour now.
  //
  // "NO CELL" IS NOT "NO NUMBER", and the whole design turns on that. `makeFlatCell`
  // returns `levelClass: 'na'` when no cell exists — the derivedContext does not
  // serve the column, or this skill does not map to that function — but returns
  // `value: 0` with a real class for a cell that DOES exist and that nobody is
  // rated in. That second one is the "Coverage gap" tooltip: a surface something
  // depends on with no one behind it, which is the most valuable thing this grid
  // shows. The predicate below mirrors the render's own two gates exactly, so it
  // can only ever drop `na` cells and can never swallow a coverage gap.
  //
  // WHOLE ROWS ONLY — never a subset of a context's skills, and that limit is
  // what keeps this presentational. `comp.skills` feeds four things the collapsed
  // `GroupedRow` displays: the "N skills" label, the institutional-knowledge
  // count, the new/deprecated diff tallies, and the coverage cell itself, which
  // is a `min` ACROSS the skill set. Filtering that array would silently change
  // those numbers — and under-reporting a new or deprecated skill would suppress
  // exactly the signal a timeline exists to show.
  //
  // Dropping a whole context touches none of it. `visibleComps` has four
  // consumers — the row-track calculation, the two empty-state checks, and the
  // render — and the change summary reads `current`/`prior` directly rather than
  // this array, so a hidden row cannot move a number anywhere.
  const hasAnyCell = useCallback(
    (compId: string, skillId: string) =>
      columns.some((col) => {
        if (!cfGate.serves(compId, col)) return false;
        const map = col.kind === 'group' ? skillFunctionsGrouped : skillFunctions;
        return map.get(skillId)?.has(col.fn.id) ?? false;
      }),
    [columns, cfGate, skillFunctions, skillFunctionsGrouped],
  );

  const visibleComps = useMemo(() => {
    // Kept whole and unmodified, or dropped entirely — see above.
    const shown = servedComps.filter((comp) =>
      comp.skills.some((sk) => hasAnyCell(comp.id, sk.id)),
    );
    // Never blank the grid: with nothing to show, the unfiltered rows explain the
    // date better than an empty screen does.
    return shown.length > 0 ? shown : servedComps;
  }, [servedComps, hasAnyCell]);

  const gridStyle = useMemo(() => {
    const base: Record<string, string> = {
      // Function columns flex down to `minmax(0, 1fr)` so the matrix shrinks
      // to fit the left content track instead of overflowing past it and
      // covering the snapshot-notes panel on the right. (A 72px floor here
      // would force the grid wider than its column whenever there are many
      // functions — that overflow is what overlapped the summary.)
      gridTemplateColumns: `130px 42px 6px 140px repeat(${columns.length}, minmax(0, 1fr))`,
    };
    // One track per rendered row (header + derivedContexts): a collapsed
    // derivedContext is a single band sized to its skill count; an expanded one
    // lists a row per skill. Explicit tracks (rather than auto-flow) let
    // collapsed and expanded derivedContexts coexist in the same grid.
    const compact = isCompactCatalog(comps);
    const rows = ['auto'];
    for (const c of visibleComps) {
      if (isCompExpanded(c.id)) {
        // Expanding is an explicit request for detail, so a per-skill row keeps
        // its readable height even in ultra-compact.
        for (let i = 0; i < c.skills.length; i += 1) rows.push('minmax(40px, auto)');
      } else if (ultraOn) {
        rows.push(ultraCompactRowHeight(c.allSkillCount) + 'px');
      } else {
        rows.push(compRowHeight(c.skills.length, compact) + 'px');
      }
    }
    base.gridTemplateRows = rows.join(' ');
    return base;
  }, [columns.length, visibleComps, comps, isCompExpanded, ultraOn]);

  const levelLabel = TALENT_LEVELS.find((l) => l.key === talentLevel)?.label ?? '';

  // Bounded scroll box so the business-function header pins on vertical
  // scroll. It fills to the viewport bottom (bottomGap 0) so the matrix
  // scrolls its full length under the fixed scrubber rather than being
  // bounded above it; an inner bottom padding lets the last rows clear
  // the scrubber when scrolled to the end. No horizontal bleed — the
  // SnapshotNotes panel sits to the right and the columns never overflow.
  const scrollRef = useFillViewportHeight<HTMLDivElement>(0);

  // Single-person Timeline: one IC's green proficiency map, scrubbed to the
  // selected snapshot. Reuses the Talent Profile grid; the slider + H2 date
  // walk that person's history.
  if (profileEngineer && current) {
    return (
      <section>
        <h1 className='snap-h1'>Timeline</h1>
        <p className='snap-subtitle'>
          {`${profileEngineer.display_name}'s proficiency: `}
          <span className='snap-subtitle__dyn snap-subtitle__dyn--plain'>
            {formatSnapshotDate(current.date)}
          </span>
        </p>

        <FloatingToolbar>
          {personFilter.show ? (
            <ToolbarMultiSelect
              label='Filter by person'
              selected={personFilter.selected}
              options={personFilter.options}
              onChange={personFilter.setSelected}
              allLabel='All people'
            />
          ) : null}
          <ToolbarSeparator />
          <ToolbarToggle
            label='Group Skills'
            pressed={grouped}
            onToggle={() => setAllGrouped(!grouped)}
          >
            <GroupSkillsIcon />
          </ToolbarToggle>
          <ToolbarToggle
            label='Show Libraries'
            pressed={techFilter.showLibraries}
            onToggle={() => techFilter.setShowLibraries(!techFilter.showLibraries)}
          >
            <ShowLibrariesIcon />
          </ToolbarToggle>
        </FloatingToolbar>

        <div className='snap-timeline'>
          <div className='snap-timeline__content'>
            <div
              ref={scrollRef}
              className='snap-matrix-sticky'
              style={{ paddingBottom: 128 }}
            >
              <ProfileMatrixGrid
                engineer={profileEngineer}
                derivedContexts={current.derivedContexts}
                businessFunctions={current.business_functions}
                ratings={current.ratings}
                grouped={grouped}
                techVisible={techFilter.visible}
              />
            </div>
          </div>
        </div>

        <TimelineScrubber
          snapshots={axis}
          index={safeIndex}
          onIndexChange={handleIndexChange}
          formatDate={formatSnapshotDate}
        />
      </section>
    );
  }

  return (
    <section>
      <h1 className='snap-h1'>Timeline</h1>
      <p className='snap-subtitle'>
        Platform snapshot:{' '}
        <span className='snap-subtitle__dyn snap-subtitle__dyn--plain'>
          {formatSnapshotDate(current?.date)}
        </span>
      </p>

      <FloatingToolbar>
        <ToolbarSelect
          label='Filter by talent level'
          value={String(talentLevel)}
          options={TALENT_LEVELS.map((l) => ({
            value: String(l.key),
            label: l.label,
          }))}
          onChange={(v) => setTalentLevel(Number(v) as RatingValue)}
        />
        {teamFilter.show ? (
          <ToolbarMultiSelect
            label='Filter by team'
            selected={teamFilter.selected}
            options={teamFilter.options}
            onChange={teamFilter.setSelected}
            allLabel='All teams'
          />
        ) : null}
        {personFilter.show ? (
          <ToolbarMultiSelect
            label='Filter by person'
            selected={personFilter.selected}
            options={personFilter.options}
            onChange={personFilter.setSelected}
            allLabel='All people'
          />
        ) : null}
        <ToolbarSeparator />
        <ToolbarToggle
          label='Group Skills'
          pressed={grouped}
          onToggle={() => setAllGrouped(!grouped)}
        >
          <GroupSkillsIcon />
        </ToolbarToggle>
        <ToolbarToggle
          label='Show Libraries'
          pressed={techFilter.showLibraries}
          onToggle={() => techFilter.setShowLibraries(!techFilter.showLibraries)}
        >
          <ShowLibrariesIcon />
        </ToolbarToggle>
        {ultraAvailable ? (
          <ToolbarToggle
            label={
              ultra
                ? 'Ultra-compact rows: on'
                : 'Ultra-compact rows — fit a large catalog on one screen'
            }
            pressed={ultra}
            onToggle={() => setUltra((v) => !v)}
          >
            <UltraCompactIcon />
          </ToolbarToggle>
        ) : null}
      </FloatingToolbar>

      <div className='snap-timeline'>
        <div className='snap-timeline__content'>
          {noRepos ? (
            <ConnectReposEmpty title='No activity yet' />
          ) : currentLoading && visibleComps.length === 0 ? (
            <EmptyState
              title='Loading snapshot…'
              description='Fetching this date’s platform map.'
            />
          ) : visibleComps.length === 0 ? (
            <EmptyState
              title='No snapshot for this date'
              description='Drag the slider to a date with recorded platform activity.'
            />
          ) : (
            <div
              ref={scrollRef}
              className='snap-matrix-sticky'
              // Inner bottom padding clears the fixed scrubber so the last
              // rows can scroll above it; content still passes under the
              // scrubber as it scrolls.
              style={{ paddingBottom: 128 }}
            >
            <div
              className={`snap-grid${ultraOn ? ' snap-grid--ultra' : ''}`}
              style={gridStyle}
            >
              <div className='snap-grid__col-header' />
              <div className='snap-grid__col-header' />
              <div className='snap-grid__col-header' />
              <div className='snap-grid__col-header' />
              {columns.map((col) => {
                const fn = col.fn;
                // A rolled-up group column carries no new/deprecated marker
                // (its children carry their own once revealed); plain/child
                // columns keep the snapshot diff treatment.
                const fnMod =
                  col.kind === 'group'
                    ? ''
                    : diff.newFnIds.has(fn.id)
                      ? ' snap-grid__col-header--new'
                      : diff.removedFnIds.has(fn.id)
                        ? ' snap-grid__col-header--deprecated'
                        : '';
                // `planned`: declared (top-down) but no code yet — its
                // first-appearance tick hasn't landed. Orthogonal to the
                // new/removed diff, so it layers on as its own modifier.
                const fnPlanned = col.kind !== 'group' && fn.state === 'planned';
                const plannedMod = fnPlanned ? ' snap-grid__col-header--planned' : '';
                // Collapsed BF group: tally new/deprecated skills rendered
                // under the group's cells, mirroring the context grouped
                // row's "(N new, M deprecated)" summary. It used to ride on the
                // child-count line; with the name owning both header lines it
                // moves into the hover reveal, under the description.
                let colNewSkills = 0;
                let colRemovedSkills = 0;
                if (col.kind === 'group') {
                  const seen = new Set<string>();
                  for (const c of merged.derivedContexts) {
                    for (const s of c.skills) {
                      if (seen.has(s.id) || !skillFunctionsGrouped.get(s.id)?.has(fn.id)) continue;
                      seen.add(s.id);
                      if (diff.newSkillIds.has(s.id)) colNewSkills += 1;
                      else if (diff.removedSkillIds.has(s.id)) colRemovedSkills += 1;
                    }
                  }
                }
                const colDeltaParts: string[] = [];
                if (colNewSkills > 0) colDeltaParts.push(`${colNewSkills} new`);
                if (colRemovedSkills > 0) colDeltaParts.push(`${colRemovedSkills} deprecated`);
                return (
                  <ColumnHeader
                    key={fn.id}
                    className={`${fnMod}${plannedMod}`}
                    display={fn.display}
                    subtitle={fn.subtitle}
                    tag={
                      fnPlanned ? (
                        <span
                          className='snap-grid__col-header__tag'
                          title='Declared, not yet built in code'
                        >
                          planned
                        </span>
                      ) : null
                    }
                    note={
                      colDeltaParts.length > 0
                        ? `${colDeltaParts.join(', ')} since the prior snapshot`
                        : null
                    }
                  />
                );
              })}

              {visibleComps.map((comp) =>
                isCompExpanded(comp.id) ? (
                  <FlatGroup
                    key={comp.id}
                    comp={comp}
                    columns={columns}
                    threshold={talentLevel}
                    levelLabel={levelLabel}
                    ratingIndex={ratingIndex}
                    skillFunctions={skillFunctions}
                    skillFunctionsGrouped={skillFunctionsGrouped}
                    cfGate={cfGate}
                    team={team}
                    diff={diff}
                    onToggleExpand={() => toggleCompExpanded(comp.id)}
                  />
                ) : (
                  <GroupedRow
                    key={comp.id}
                    comp={comp}
                    ultra={ultraOn}
                    columns={columns}
                    threshold={talentLevel}
                    levelLabel={levelLabel}
                    ratingIndex={ratingIndex}
                    skillFunctions={skillFunctions}
                    skillFunctionsGrouped={skillFunctionsGrouped}
                    cfGate={cfGate}
                    team={team}
                    diff={diff}
                    onToggleExpand={() => toggleCompExpanded(comp.id)}
                  />
                ),
              )}
            </div>
            </div>
          )}
        </div>

        {SHOW_ACTIVITY_FEED ? (
          <ControlPanel title='Activity'>
            <ActivityList
              hasMore={hasMoreActivity}
              isLoadingMore={isLoadingMoreActivity}
              onLoadMore={onLoadMoreActivity}
              items={activityItems}
              derivedContexts={activityDerivedContexts}
              functions={activityFunctions}
              // Repo-connect lives in the main content panel; the activity
              // bar just states the empty fact so links aren't duplicated.
              emptyState={<EmptyState title='No activity yet' compact />}
            />
          </ControlPanel>
        ) : (
          <SnapshotNotes
            date={formatLongSnapshotDate(current?.date ?? currentDate)}
            topline={snapshotNotes.topline}
            notes={snapshotNotes.notes}
            {...(current?.structure ? { structure: current.structure } : {})}
          />
        )}
      </div>

      <TimelineScrubber
        snapshots={axis}
        index={safeIndex}
        onIndexChange={handleIndexChange}
        formatDate={formatSnapshotDate}
      />
    </section>
  );
}

interface RowProps {
  comp: DerivedContext;
  columns: RollupColumn[];
  threshold: RatingValue;
  levelLabel: string;
  ratingIndex: ReturnType<typeof buildRatingIndex>;
  skillFunctions: ReturnType<typeof buildSkillFunctions>;
  skillFunctionsGrouped: ReturnType<typeof buildSkillFunctions>;
  /** Shared platform-map cell gate (blocks shared-skill phantom cells). */
  cfGate: DerivedContextFunctionGate;
  team: Engineer[];
  diff: SnapshotDiff;
  /** Ultra-compact rows: drops the "N proprietary" line (the green dot stays).
   *  Only ever true on the collapsed `GroupedRow`. */
  ultra?: boolean;
  /** Toggle this derivedContext between the collapsed band and its expanded
   *  skill list — wired to the connector line. */
  onToggleExpand: () => void;
}

/** Adjacency map a column's cells use: grouped (child→parent) for a roll-up
 *  group column, otherwise the flat map. */
function mapForColumn(
  col: RollupColumn,
  flat: ReturnType<typeof buildSkillFunctions>,
  grouped: ReturnType<typeof buildSkillFunctions>,
): ReturnType<typeof buildSkillFunctions> {
  return col.kind === 'group' ? grouped : flat;
}

// Map a (comp, skill, fn) triple to the extra class applied to its
// cell. Removed-anything wins over new-anything; column-level state
// wins over row-level. Empty string = no diff marker.
function cellDiffClass(
  compNew: boolean,
  compRemoved: boolean,
  skillNew: boolean,
  skillRemoved: boolean,
  fnNew: boolean,
  fnRemoved: boolean,
): string {
  if (compRemoved || skillRemoved || fnRemoved) return 'is-deprecated';
  if (compNew || skillNew || fnNew) return 'is-new';
  return '';
}

function FlatGroup({
  comp,
  columns,
  threshold,
  levelLabel,
  ratingIndex,
  skillFunctions,
  skillFunctionsGrouped,
  cfGate,
  team,
  diff,
  onToggleExpand,
}: RowProps) {
  const compNew = diff.newCompIds.has(comp.id);
  const compRemoved = diff.removedCompIds.has(comp.id);
  const compMod = compNew
    ? ' snap-grid__row-label--new'
    : compRemoved
      ? ' snap-grid__row-label--deprecated'
      : '';
  const multiSkill = comp.skills.length > 1;
  return (
    <>
      {comp.skills.map((skill, i) => {
        const skillNew = diff.newSkillIds.has(skill.id);
        const skillRemoved = diff.removedSkillIds.has(skill.id);
        const skillMod = skillNew
          ? ' snap-grid__row-label--new'
          : skillRemoved
            ? ' snap-grid__row-label--deprecated'
            : '';
        return (
          <Fragment key={skill.id}>
            {i === 1 && multiSkill ? null : (
              <div
                className={`snap-grid__row-label snap-grid__row-label--muted${compMod}${
                  i === 0 && multiSkill ? ' snap-grid__row-label--wrap-2' : ''
                }`}
                style={i === 0 && multiSkill ? { gridRow: 'span 2' } : undefined}
              >
                {i === 0 ? (
                  <TruncatedLabel text={comp.display} max={multiSkill ? 60 : 15} />
                ) : null}
              </div>
            )}
            <ConnectorCell
              variant={nestVariant(i, comp.skills.length)}
              onToggle={onToggleExpand}
            />
            <div className='snap-grid__row-label snap-grid__row-label--prop'>
              <PropIndicator institutional={isInstitutional(skill)} />
            </div>
            <div className={`snap-grid__row-label${skillMod}`}>
              <TruncatedLabel text={skill.display} />
            </div>
            {columns.map((col) => {
              const fn = col.fn;
              const sf = mapForColumn(col, skillFunctions, skillFunctionsGrouped);
              // A group column carries no per-function diff (children carry
              // their own once revealed).
              const fnNew = col.kind === 'group' ? false : diff.newFnIds.has(fn.id);
              const fnRemoved =
                col.kind === 'group' ? false : diff.removedFnIds.has(fn.id);
              // Shared platform-map gate: a cell exists only where the derivedContext
              // itself serves the function (not via the per-skill union). No real
              // intersection → gray cell regardless of new/deprecated state.
              if (!cfGate.serves(comp.id, col)) {
                return (
                  <MatrixCell
                    key={fn.id}
                    value={null}
                    levelClass='na'
                    tooltip={null}
                  />
                );
              }
              const extra = cellDiffClass(
                compNew,
                compRemoved,
                skillNew,
                skillRemoved,
                fnNew,
                fnRemoved,
              );
              const coverage = coverageForCell(
                comp,
                fn,
                threshold,
                ratingIndex,
                sf,
                team,
              );
              const entry = coverage.find((c) => c.skill.id === skill.id);
              const value = entry?.covered.length ?? 0;
              return (
                <MatrixCell
                  key={fn.id}
                  value={value}
                  levelClass={levelClass(value)}
                  extraClass={extra || undefined}
                  tooltip={{
                    title:
                      value === 0
                        ? 'Coverage gap'
                        : `${value} engineer${value === 1 ? '' : 's'}`,
                    body:
                      value === 0
                        ? `No one rated ${levelLabel}.`
                        : (entry?.covered ?? [])
                            .map(
                              (e) =>
                                `${e.engineer.display_name} — ${RATING_LABELS[e.rating]}`,
                            )
                            .join('\n'),
                  }}
                />
              );
            })}
          </Fragment>
        );
      })}
    </>
  );
}

function GroupedRow({
  comp,
  columns,
  threshold,
  levelLabel,
  ratingIndex,
  skillFunctions,
  skillFunctionsGrouped,
  cfGate,
  team,
  diff,
  ultra = false,
  onToggleExpand,
}: RowProps) {
  const compNew = diff.newCompIds.has(comp.id);
  const compRemoved = diff.removedCompIds.has(comp.id);
  // Diff treatment now rides on the name cell (blue+bold for new, struck
  // muted for removed) via the shared row-label diff classes.
  const nameMod = compNew
    ? 'snap-grid__row-label--new'
    : compRemoved
      ? 'snap-grid__row-label--deprecated'
      : undefined;
  // Per-comp skill-change tally. `comp.skills` is the merged list
  // (current + deprecated-from-prior), so the live count is the merged
  // total minus what got deprecated this step.
  const newSkillsInComp = comp.skills.reduce(
    (n, s) => (diff.newSkillIds.has(s.id) ? n + 1 : n),
    0,
  );
  const deprecatedSkillsInComp = comp.skills.reduce(
    (n, s) => (diff.removedSkillIds.has(s.id) ? n + 1 : n),
    0,
  );
  const liveSkillCount = comp.skills.length - deprecatedSkillsInComp;
  const summaryParts: string[] = [];
  if (newSkillsInComp > 0) summaryParts.push(`${newSkillsInComp} new`);
  if (deprecatedSkillsInComp > 0)
    summaryParts.push(`${deprecatedSkillsInComp} deprecated`);
  const hasChanges = summaryParts.length > 0;
  return (
    <>
      <GroupedCompLabel
        nameClassName={nameMod}
        propCount={comp.skills.filter(isInstitutional).length}
        hidePropCount={ultra}
        onToggle={onToggleExpand}
        expanded={false}
        name={<TruncatedLabel text={comp.display} max={22} />}
        count={
          <>
            <span
              className={
                hasChanges ? 'snap-grid__group-count__n--changed' : undefined
              }
            >
              {liveSkillCount} skill{liveSkillCount === 1 ? '' : 's'}
            </span>
            {hasChanges ? (
              <span className='snap-grid__group-count__delta'>
                {' '}
                ({summaryParts.join(', ')})
              </span>
            ) : null}
          </>
        }
      />
      {columns.map((col) => {
        const fn = col.fn;
        // Shared platform-map gate: only a genuinely-served (comp × fn) cell
        // renders coverage; others stay gray.
        const cell = cfGate.serves(comp.id, col)
          ? makeMergedCoverageCell(
              comp,
              fn,
              threshold,
              levelLabel,
              ratingIndex,
              mapForColumn(col, skillFunctions, skillFunctionsGrouped),
              team,
              // A skill on its way out must not set the derivedContext's floor —
              // `mergeSnapshotsForDiff` re-attaches removed skills to their
              // derivedContext so they render struck-through, and nobody is rated
              // on them, so an unfiltered `min` read 0 for the whole group.
              diff.removedSkillIds,
            )
          : { value: null, levelClass: 'na', tooltip: null };
        const fnNew = col.kind === 'group' ? false : diff.newFnIds.has(fn.id);
        const fnRemoved = col.kind === 'group' ? false : diff.removedFnIds.has(fn.id);
        const extra =
          cell.levelClass === 'na'
            ? ''
            : cellDiffClass(compNew, compRemoved, false, false, fnNew, fnRemoved);
        return (
          <MatrixCell
            key={fn.id}
            {...cell}
            extraClass={`is-merged${extra ? ' ' + extra : ''}`}
          />
        );
      })}
    </>
  );
}
